// Right-pane profile detail view. Shows identity, MT5 key (with copy/rotate),
// data dir, backend runtime controls, telegram login, the per-chat trading
// config card (driven by the chat selected in the sidebar), trade audit,
// playground, and live logs. Matches the desktop's tg-pane + tg-surface
// visual language.
import { useEffect, useState } from "react";
import type { UserVisibilityFlags } from "@shared/api/schemas/auth";
import type { BackendSupervisorState } from "@shared/api/schemas/backend";
import type { DesktopProfileSummary } from "@shared/api/schemas/profiles";
import type { TelegramChannelSummary } from "@shared/api/schemas/telegram";
import type { TradingChannelConfig } from "@shared/api/schemas/trading-channels";
import { ApiError, httpClient } from "../../client/http-client";
import { wsClient } from "../../client/ws-client";
import { useLiveResource } from "../../hooks/useLiveResource";
import {
	CopyIcon,
	PencilIcon,
	RefreshIcon,
	SettingsIcon,
	TrashIcon,
} from "../../components/Icons";
import { ThemeToggleButton } from "../../components/ThemeToggleButton";
import type { ThemeMode } from "../../lib/theme";
import { BackendStatusCard } from "./BackendStatusCard";
import { LogViewer } from "./LogViewer";
import { Mt5RuntimeSection } from "./Mt5RuntimeSection";
import { PlaygroundSection } from "./PlaygroundSection";
import { SettingsModal } from "./SettingsModal";
import { TelegramLoginCard } from "./TelegramLoginCard";
import { TradeAuditSection } from "./TradeAuditSection";
import { TradingChannelsSection } from "./TradingChannelsSection";

interface ProfileDetailProps {
	profile: DesktopProfileSummary;
	themeMode: ThemeMode;
	telegramChannels: TelegramChannelSummary[];
	telegramReady: boolean;
	tradingChannels: TradingChannelConfig[];
	selectedChatId: number | null;
	/** True when the logged-in user has admin role. Threaded down so the
	 *  MT5 runtime card can show the EA download + pod-log panel. */
	isAdmin: boolean;
	/** Per-user opt-in flags for the 3 debug-level cards (Logs,
	 *  Playground, MT5 connection). Cards are hidden by default for
	 *  non-admins; admins always see everything regardless of flags. */
	visibilityFlags: UserVisibilityFlags;
	onTradingChannelsChange: (next: TradingChannelConfig[]) => void;
	onToggleTheme: () => void;
	onProfilesChanged: () => void;
	onProfileDeleted: () => void;
}

export const ProfileDetail = ({
	profile,
	themeMode,
	telegramChannels,
	telegramReady,
	tradingChannels,
	selectedChatId,
	isAdmin,
	visibilityFlags,
	onTradingChannelsChange,
	onToggleTheme,
	onProfilesChanged,
	onProfileDeleted,
}: ProfileDetailProps) => {
	const [mt5Key, setMt5Key] = useState<string>(profile.mt5ApiKey ?? "");
	const [copiedKey, setCopiedKey] = useState(false);
	const [renaming, setRenaming] = useState(false);
	const [nameDraft, setNameDraft] = useState(profile.name);
	const [busy, setBusy] = useState<"rename" | "delete" | "rotate" | null>(
		null,
	);
	const [error, setError] = useState<string | null>(null);
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);

	// Reset local state when the selected profile changes.
	useEffect(() => {
		setMt5Key(profile.mt5ApiKey ?? "");
		setNameDraft(profile.name);
		setRenaming(false);
	}, [profile.id, profile.mt5ApiKey, profile.name]);

	// Backend supervisor state, live-synced via useLiveResource:
	//   - REST fetch on mount + profile switch
	//   - WS `backend.stateChanged` for live transitions
	//   - re-fetch on WS reconnect (orchestrator pod roll resilience)
	const { value: backendState } = useLiveResource<BackendSupervisorState>({
		key: profile.id,
		fetcher: () => httpClient.backend.getState(profile.id),
		subscribe: (onUpdate) =>
			wsClient.onBackendChanged((incomingProfileId, state) => {
				if (incomingProfileId === profile.id) onUpdate(state);
			}),
		clearOnError: true,
	});

	const handleCopyKey = async () => {
		if (!mt5Key) return;
		try {
			await navigator.clipboard.writeText(mt5Key);
			setCopiedKey(true);
			setTimeout(() => setCopiedKey(false), 1500);
		} catch {
			setError("Couldn’t copy to clipboard.");
		}
	};

	const handleRotateKey = async () => {
		setBusy("rotate");
		setError(null);
		try {
			const response = await httpClient.profiles.rotateMt5Key(profile.id);
			setMt5Key(response.mt5ApiKey);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setBusy(null);
		}
	};

	const handleRename = async () => {
		const next = nameDraft.trim();
		if (!next || next === profile.name) {
			setRenaming(false);
			setNameDraft(profile.name);
			return;
		}
		setBusy("rename");
		setError(null);
		try {
			await httpClient.profiles.rename(profile.id, next);
			setRenaming(false);
			onProfilesChanged();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setBusy(null);
		}
	};

	const handleDelete = async () => {
		const confirmed = window.confirm(
			`Delete profile "${profile.name}"? On-disk data (TDLib session, logs, signals.db) is left intact on the server.`,
		);
		if (!confirmed) return;
		setBusy("delete");
		setError(null);
		try {
			await httpClient.profiles.delete(profile.id);
			onProfileDeleted();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setBusy(null);
		}
	};

	return (
		<main className="tg-pane relative grid h-full min-h-0 grid-rows-[auto_auto_1fr] gap-4 overflow-hidden px-6 py-5">
			<div className="absolute top-4 right-5 flex items-center gap-2">
				<button
					type="button"
					onClick={() => setIsSettingsOpen(true)}
					className="tg-btn-icon tg-btn-secondary tg-text-secondary rounded-xl"
					aria-label="Profile settings"
					title="Profile settings"
				>
					<SettingsIcon className="h-5 w-5" />
				</button>
				<ThemeToggleButton
					isLightTheme={themeMode === "light"}
					onToggle={onToggleTheme}
				/>
			</div>

			<SettingsModal
				profileId={profile.id}
				isOpen={isSettingsOpen}
				onClose={() => setIsSettingsOpen(false)}
			/>

			<header className="grid grid-cols-[1fr_auto] items-start gap-3 pr-28">
				<div className="min-w-0">
					{renaming ? (
						<form
							onSubmit={(event) => {
								event.preventDefault();
								void handleRename();
							}}
							className="flex items-center gap-2"
						>
							<input
								className="tg-input tg-text-primary h-10 flex-1 rounded-xl px-3 text-[1.05rem] font-semibold"
								autoFocus
								value={nameDraft}
								onChange={(event) => setNameDraft(event.target.value)}
								disabled={busy !== null}
							/>
							<button
								type="submit"
								className="tg-btn-primary inline-flex h-9 items-center rounded-lg px-3 text-[0.85rem] font-semibold disabled:cursor-wait disabled:opacity-60"
								disabled={busy !== null}
							>
								{busy === "rename" ? "Saving..." : "Save"}
							</button>
							<button
								type="button"
								className="tg-btn-text text-[0.85rem] font-semibold"
								onClick={() => {
									setRenaming(false);
									setNameDraft(profile.name);
								}}
								disabled={busy !== null}
							>
								Cancel
							</button>
						</form>
					) : (
						<div className="flex items-center gap-3">
							<h1 className="tg-text-primary truncate text-[1.7rem] font-bold leading-tight">
								{profile.name}
							</h1>
							<button
								type="button"
								onClick={() => setRenaming(true)}
								className="tg-btn-icon tg-btn-secondary tg-text-secondary inline-flex h-8 w-8 rounded-lg"
								aria-label="Rename profile"
								title="Rename"
							>
								<PencilIcon className="h-4 w-4" />
							</button>
						</div>
					)}
					<p className="tg-text-muted mt-1 truncate font-mono text-[0.8rem]">
						id: {profile.id}
					</p>
				</div>

				<button
					type="button"
					onClick={() => void handleDelete()}
					className="tg-btn-danger inline-flex h-9 items-center gap-2 rounded-xl px-3 text-[0.85rem] font-semibold disabled:cursor-wait disabled:opacity-60"
					disabled={busy !== null}
				>
					<TrashIcon className="h-4 w-4" />
					<span>{busy === "delete" ? "Deleting profile…" : "Delete profile"}</span>
				</button>
			</header>

			{error ? (
				<div className="tg-alert-error rounded-xl px-4 py-3" role="alert">
					{error}
				</div>
			) : null}

			<div className="grid min-h-0 auto-rows-min gap-4 overflow-y-auto pr-1">
				<section className="tg-surface rounded-2xl p-4">
					<h3 className="tg-text-primary text-[0.95rem] font-semibold leading-5">
						MT5 connection
					</h3>
					<p className="tg-text-muted mt-1 text-[0.82rem] leading-5">
						Paste this key into your Expert Advisor’s <code className="tg-code-block inline rounded px-1">apiKey</code>{" "}
						input and use{" "}
						<code className="tg-code-block inline rounded px-1">profileId</code>{" "}
						= the profile id above. The orchestrator will route{" "}
						<code className="tg-code-block inline rounded px-1">
							/mt5/{profile.id.slice(0, 8)}…
						</code>{" "}
						to this profile’s backend.
					</p>

					<div className="mt-4 grid grid-cols-[auto_1fr_auto_auto] items-center gap-3">
						<span className="tg-text-muted text-[0.85rem]">X-API-Key</span>
						<code className="tg-code-block truncate rounded-xl px-3 py-2 font-mono text-[0.85rem]">
							{mt5Key || "—"}
						</code>
						<button
							type="button"
							onClick={() => void handleCopyKey()}
							disabled={!mt5Key}
							className="tg-btn-secondary tg-text-secondary inline-flex h-9 items-center gap-2 rounded-xl px-3 text-[0.82rem] font-semibold disabled:opacity-55"
						>
							<CopyIcon className="h-4 w-4" />
							<span>{copiedKey ? "Copied" : "Copy"}</span>
						</button>
						<button
							type="button"
							onClick={() => void handleRotateKey()}
							disabled={busy !== null}
							className="tg-btn-secondary tg-text-secondary inline-flex h-9 items-center gap-2 rounded-xl px-3 text-[0.82rem] font-semibold disabled:opacity-55"
						>
							<RefreshIcon className="h-4 w-4" />
							<span>{busy === "rotate" ? "Rotating..." : "Rotate"}</span>
						</button>
					</div>

					{/*
					 * EA download moved into Mt5RuntimeSection below — it's
					 * admin-only now, since regular users get a managed MT5
					 * pod via "Start MT5" rather than running their own MT5.
					 */}
				</section>

				{(isAdmin || visibilityFlags.showMt5ConnectionCard) && (
					<Mt5RuntimeSection profileId={profile.id} isAdmin={isAdmin} />
				)}

				<BackendStatusCard profileId={profile.id} state={backendState} />

				<TelegramLoginCard profileId={profile.id} />

				<TradingChannelsSection
					profileId={profile.id}
					channels={telegramChannels}
					telegramReady={telegramReady}
					selectedChatId={selectedChatId}
					tradingChannels={tradingChannels}
					onTradingChannelsChange={onTradingChannelsChange}
				/>

				<TradeAuditSection profileId={profile.id} />

				{(isAdmin || visibilityFlags.showPlaygroundCard) && (
					<PlaygroundSection profileId={profile.id} />
				)}

				{(isAdmin || visibilityFlags.showLogsCard) && (
					<LogViewer profileId={profile.id} />
				)}
			</div>
		</main>
	);
};
